import { type DocumentReference } from 'firebase/firestore';

import { getAppBroker } from 'stores/AppBroker';
import { requireMatchDocumentFolder } from 'stores/DatabasePaths';
import DocumentWithMembershipStore from 'stores/DocumentWithMembershipStore';
import { copyMemberAccess, type MemberAccess } from 'types/Roles';

import BreadcrumbManager from './BreadcrumbManager';
import DocumentFolderCollection from './DocumentFolderCollection';
import { type FolderPublicSchema, FolderSchema } from './DocumentFolderTypes';
import type { RootStore } from './RootStore';
import type StarredDocuments from './StarredDocuments';

/**
 * Fills in the folder defaults that can only be known at runtime: membership
 * copied from the containing folder (if any), and the current user as author.
 */
export function applyDynamicFolderDefaults(
    data: MemberAccess & { author: string },
    parentFolder: { data: MemberAccess } | null | undefined
): void {
    if (parentFolder) copyMemberAccess(parentFolder.data, data);
    data.author = getAppBroker().getCurrentUser()?.id ?? '';
}

/**
 * What a folder is constructed with: the DocumentFolderCollection holding it,
 * the root when RootStore loads one directly, or StarredDocuments for the
 * stubs it makes. Subclasses take the same.
 */
export type DocumentFolderParent =
    | DocumentFolderCollection
    | RootStore
    | StarredDocuments;

/**
 * A DocumentFolder is a DocumentStore that contains other DocumentStores and DocumentCollections. These documents are stored in the `documents` property.
 * @class DocumentFolder
 * @extends DocumentStore
 * @param {DocumentFolderCollection} parent - The parent DocumentFolderCollection
 * @param {DocumentReference} docRef - The DocumentReference for the DocumentFolder
 * @param {FolderSchema} data - The data for the DocumentFolder
 * @param {boolean} stub - Whether the DocumentFolder is a stub or not
 */
export default class DocumentFolder<
    Schema extends FolderSchema = FolderSchema,
    PublicSchema extends object = FolderPublicSchema,
> extends DocumentWithMembershipStore<Schema, PublicSchema> {
    stub = false;
    breadcrumbs: BreadcrumbManager = new BreadcrumbManager(this);
    get name() {
        return this.data?.name;
    }
    // childrenType = DocumentType.PROJECT;

    documents: DocumentFolderCollection = new DocumentFolderCollection(
        this,
        DocumentWithMembershipStore
    );

    /**
     * The folder whose `documents` collection holds this one. Undefined for
     * top-level clients, for folders loaded directly by RootStore (whose
     * parent is the root), and after reset() clears `parent`.
     */
    get parentFolder(): DocumentFolder<any, any> | undefined {
        const collection = this.parent;
        if (!(collection instanceof DocumentFolderCollection)) return undefined;
        const owner = collection.parent;
        return owner instanceof DocumentFolder ? owner : undefined;
    }

    public getDefaults() {
        const data = new FolderSchema();
        applyDynamicFolderDefaults(data, this.parentFolder);
        return data as Schema;
    }

    /**
     * Returns the params for the DocumentFolder. This is a combination of the parent DocumentFolderCollection's params and the DocumentFolder's id.
     * @returns The params for the DocumentFolder
     * */
    get params(): Record<string, string> {
        return requireMatchDocumentFolder(this.docRef.path);
    }

    /**
     * Returns the url for the DocumentFolder.
     * @returns {string} The url for the DocumentFolder
     * */
    get url(): string {
        return getAppBroker().routeForDocument(this.params, {
            documentTypeHint: this.type,
        });
    }

    /**
     * Returns the tags for the DocumentFolder's documents as an array of strings.
     * @returns {string[]} The tags for the DocumentFolder's documents
     * */
    get documentsTags() {
        const allTags = this.documents.all
            .map((p) => p.data.tags)
            .flat() as string[];
        const uniqueTags = [...new Set(allTags)];
        return uniqueTags;
    }

    /**
     * Returns whether the DocumentFolder is starred or not. This checks the user's starred collection for the DocumentFolder.
     * @returns {boolean} Whether the DocumentFolder is starred or not
     * */
    get isStarred(): boolean {
        const user = getAppBroker().getCurrentUser();
        return user?.starred.get(this) != null;
    }

    constructor(parent: DocumentFolderParent, docRef?: DocumentReference) {
        super(parent, docRef);
        // this.becomeObservable();
    }

    private setStarred(newIsStarred: boolean) {
        const user = getAppBroker().getCurrentUser();
        if (!user) return;
        if (newIsStarred) {
            user.starred.add(this);
        } else {
            user.starred.remove(this);
        }
    }

    /**
     * Toggles whether the DocumentFolder is starred or not. This checks the user's starred collection for the DocumentFolder and adds or removes it accordingly.
     * */
    toggleStarred = () => {
        console.log('toggleStarred');
        this.setStarred(!this.isStarred);
    };

    /**
     * Adds the DocumentFolder to the user's starred collection.
     * */
    addStarred = () => {
        this.setStarred(true);
    };

    /**
     * Removes the DocumentFolder from the user's starred collection.
     * */
    removeStarred = () => {
        this.setStarred(false);
    };

    /**
     * Adds a tag string to the DocumentFolder's tags array located in the document `data` property.
     * @param {string} tagName - The tag name to add to the DocumentFolder's tags array
     * */
    addTag = async (tagName: string) => {
        const tags = [...this.data.tags];
        if (tags.indexOf(tagName) > -1) return;
        tags.push(tagName);
        this.data.tags = tags;
        await this.save();
    };

    /**
     * Removes a tag string from the DocumentFolder's tags array located in the document `data` property.
     * @param {string} tagName - The tag name to remove from the DocumentFolder's tags array
     * */
    removeTag = (tagName: string) => {
        const tags = this.data.tags.filter((tag) => tag !== tagName);
        this.data.tags = tags;
        this.save();
    };

    /**
     * This overrides the base `onLoad` method and is called when the DocumentFolder is loaded. This method loads the DocumentFolder's members and documents collections.
     * @param {FolderSchema} _data - The current data of the document when it's loaded
     * */
    onLoad = async (_data: FolderSchema) => {
        await this.breadcrumbs.load();
        if (!this.stub) {
            await this.members.load();
            await this.documents.load();
        }
    };

    onUpdated = (_data: Schema, _oldData: Schema) => {
        this.members.update();
    };

    onReady = () => {};

    /**
     * This overrides the base `onReset` method and is called when the Document `reset()` method is called. This method resets the DocumentFolder's members, collections, and breadcrumbs.
     * */
    onReset = () => {
        this.members.reset();
        this.documents.reset();
        this.breadcrumbs.reset();
    }
    
    /**
     * Archives the DocumentFolder by setting the `archived` property to `true` and saving the data to Firestore.
     * */
    archive = async () => {
        this.data.archived = true;
        await this.save();
    }
    
    /**
     * Unarchives the DocumentFolder by setting the `archived` property to `false` and saving the data to Firestore.
     * */
    unarchive = async () => {
        this.data.archived = false;
        await this.save();
    }
    
    /**
     * Toggles whether the DocumentFolder is archived or not. This checks the DocumentFolder's `archived` property and calls the `archive` or `unarchive` methods accordingly.
     * */
    toggleArchive = async () => {
        if (this.data.archived) {
            return this.unarchive();
        }

        return this.archive();
    }
}
